import { supabase } from '@/lib/supabase'

export type UsageDailyEntry = {
  date: string
  calls: number
  input_tokens: number
  output_tokens: number
  cost_usd: number
}

export type UsageRecentEntry = {
  id: string
  model: string
  input_tokens: number
  output_tokens: number
  cost_usd: number
  endpoint: string
  created_at: string
  request_body: string | null
  response_body: string | null
}

export type UsageStats = {
  total_calls: number
  total_input_tokens: number
  total_output_tokens: number
  total_cost_usd: number
  daily: UsageDailyEntry[]
  recent: UsageRecentEntry[]
}

export function getDateFilter(period: string): string | null {
  if (period === '7d') {
    return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  } else if (period === '30d') {
    return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
  }
  return null
}

export type UsageLogRow = {
  id: string
  model: string
  input_tokens: number
  output_tokens: number
  cost_usd: number
  endpoint: string
  created_at: string
  request_body: string | null
  response_body: string | null
}

export function aggregateUsageLogs(rows: UsageLogRow[]): UsageStats {
  let totalCalls = 0
  let totalInputTokens = 0
  let totalOutputTokens = 0
  let totalCostUsd = 0

  const dailyMap = new Map<string, {
    calls: number
    input_tokens: number
    output_tokens: number
    cost_usd: number
  }>()

  for (const row of rows) {
    totalCalls++
    totalInputTokens += row.input_tokens
    totalOutputTokens += row.output_tokens
    totalCostUsd += row.cost_usd

    const date = new Date(row.created_at).toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' })
    const entry = dailyMap.get(date) || { calls: 0, input_tokens: 0, output_tokens: 0, cost_usd: 0 }
    entry.calls++
    entry.input_tokens += row.input_tokens
    entry.output_tokens += row.output_tokens
    entry.cost_usd += row.cost_usd
    dailyMap.set(date, entry)
  }

  const daily = Array.from(dailyMap.entries()).map(([date, data]) => ({
    date,
    ...data,
  }))

  const recent = [...rows].reverse().slice(0, 50).map(row => ({
    id: row.id,
    model: row.model,
    input_tokens: row.input_tokens,
    output_tokens: row.output_tokens,
    cost_usd: row.cost_usd,
    endpoint: row.endpoint,
    created_at: row.created_at,
    request_body: row.request_body,
    response_body: row.response_body,
  }))

  return {
    total_calls: totalCalls,
    total_input_tokens: totalInputTokens,
    total_output_tokens: totalOutputTokens,
    total_cost_usd: totalCostUsd,
    daily,
    recent,
  }
}

export async function fetchUsageStats(period: string = '30d'): Promise<UsageStats> {
  const dateFilter = getDateFilter(period)

  let query = supabase
    .from('api_usage_logs')
    .select('*')
    .order('created_at', { ascending: true })

  if (dateFilter) {
    query = query.gte('created_at', dateFilter)
  }

  const { data: logs } = await query

  return aggregateUsageLogs((logs ?? []) as UsageLogRow[])
}
